define([
    'bootstrap/offcanvas',
    'bootstrap/collapse',
    'hoverintent'
], (Offcanvas, Collapse, hoverintent) => {
    'use strict';

    const parentItemSelector = '-item--parent';

    class NavigationEvo {
        /**
         * @param {object} config navigation evo configuration
         */
        constructor (config) {
            this.menuId = config.menuId;

            // set default undefined for internal properties
            this._offcanvas = void 0;
            this._menuEl = void 0;

            if (!this.menuEl) {
                return;
            }

            this.menuEl.addEventListener('hidden.bs.offcanvas', e => {
                const openSubOffcanvases = [...e.target.querySelectorAll('.show.offcanvas,.show[class*="offcanvas-"]')]
                    .map(subEl => Offcanvas.getInstance(subEl))
                    .filter(x => Boolean(x));
                openSubOffcanvases.forEach(oc => oc.hide());
                // in the case that we've caught a submenu's hide, restore main menu's focustrap
                if (this.offcanvas._isShown) {
                    const focustrap = this.offcanvas._focustrap;
                    focustrap.deactivate();
                    focustrap.activate();
                }

            });

            this.menuEl.addEventListener('shown.bs.offcanvas', e => {
                if (e.target.classList.contains('offcanvas-nav-submenu') && e.target.parentElement.classList.contains('offcanvas-nav-category-link')) {
                    const firstCollapse = e.target.querySelector('.collapse');
                    firstCollapse && Collapse.getOrCreateInstance(firstCollapse).show();
                }
            });

            const closeNonCurrectCollapses = e => {
                /** @type Array<HTMLElement> */
                const openCollapses = [...e.currentTarget.querySelectorAll('.collapse.show')];
                openCollapses
                    .filter(el => e.target !== el)
                    .forEach(collapse => Collapse.getOrCreateInstance(collapse).hide());
            };
            this.menuEl.addEventListener('show.bs.collapse', closeNonCurrectCollapses);
            this.menuEl.addEventListener('hidden.bs.offcanvas', closeNonCurrectCollapses);

            [...this.menuEl.querySelectorAll('.event-target')].forEach(el => this.attachHoverIntent(el));
            [...this.menuEl.querySelectorAll('.offcanvas-focustrap-off')].forEach(
                el => el.addEventListener('click', e => this.offcanvas._focustrap.deactivate())
            );
            this.renderSubmenuTriggers();
            this.renderCollapseTriggers();
        }

        /**
         * @returns {HTMLElement|null} menu element, if any
         */
        get menuEl () {
            if (typeof this._menuEl === 'undefined') {
                this._menuEl = document.getElementById(this.menuId);
            }
            return this._menuEl;
        }

        get offcanvas () {
            if (typeof this._offcanvas === 'undefined') {
                this._offcanvas = Offcanvas.getOrCreateInstance(this.menuEl);
            }
            return this._offcanvas;
        }

        get activeMenusWithSubmenu () {
            return this.menuEl.querySelectorAll(`.active[class*="${parentItemSelector}"]`);
        }

        /**
         * @param {Element} el element to add interactions
         */
        attachHoverIntent (el) {
            // elements with the class substring "-item--parent" have a child menu
            const hasSubmenu = el.classList.value.indexOf(parentItemSelector) > -1;

            const onOut = (target) => {
                if (this.offcanvas._isShown) {
                    return;
                }
                target.classList.remove('active');
                if (this.activeMenusWithSubmenu.length === 0) {
                    this.menuEl.classList.remove('submenu-active');
                }
            };
            const onOver = (target) => {
                if (this.offcanvas._isShown) {
                    return;
                }
                [...this.activeMenusWithSubmenu].forEach(_el => onOut(_el));
                target.classList.add('active');
                if (hasSubmenu) {
                    this.menuEl.classList.add('submenu-active');
                }
            };
            hoverintent(el, () => onOver(el), () => onOut(el)).options({timeout: 300, interval: 30});

            if (hasSubmenu) {
                // find the element's direct anchor child and subvert clicks to show the child menu
                const anchor = el.querySelector(':scope > a');
                if (anchor) {
                    anchor.addEventListener('click', e => {
                        e.stopPropagation();
                        e.preventDefault();
                        el.classList.contains('active') ? onOut(el) : onOver(el);
                    });
                }
                el.addEventListener('focusout', e => {
                    if ([...this.activeMenusWithSubmenu].find(activeMenu => activeMenu === el) && !el.contains(e.relatedTarget)) {
                        onOut(el);
                    }
                });
            }
        }
        renderCollapseTriggers () {
            /** @type Array<HTMLElement> */
            const collapses = [...(this.menuEl.querySelectorAll('.offcanvas-nav-collapse'))];
            collapses
                .map(collapse => [collapse, collapse.querySelector('a')])
                .filter(([collapse, maybeAnchor]) => maybeAnchor instanceof HTMLAnchorElement)
                .forEach(([collapse, anchor]) => {
                    collapse.insertAdjacentHTML(
                        "beforebegin",
                        `<a href="#${collapse.id}" class="offcanvas-nav-accordion-toggle" data-bs-toggle="collapse"
                            ${(collapse.dataset.bsTriggerLabel ? `aria-label="${collapse.dataset.bsTriggerLabel}"` : '')}
                            aria-controls="${collapse.id}" aria-expanded="false">
                             ${anchor.innerHTML}
                             <i class="icon-plus"></i>
                             <i class="icon-subtract"></i>
                         </a>`
                    );
                    /** @type Array<HTMLElement> */
                    const collapseItems = [...collapse.querySelectorAll('.offcanvas-nav-collapse-item')];
                    collapseItems.forEach((item, i) => {
                        item.style.setProperty('--lfi-accordion-item-index', String(i));
                    });
                });
        }

        renderSubmenuTriggers () {
            /** @type Array<HTMLElement> */
            const submenus = [...(this.menuEl.querySelectorAll('.offcanvas-nav-submenu'))];
            submenus
                .map(submenu => [submenu, submenu.previousElementSibling])
                .filter(([submenu, maybeAnchor]) => maybeAnchor instanceof HTMLAnchorElement)
                .forEach(([submenu, anchor]) => anchor.insertAdjacentHTML(
                    "afterend",
                    `<a href="#${submenu.id}" class="offcanvas-nav-open-submenu"
                             ${(submenu.dataset.bsTriggerLabel ? `aria-label="${submenu.dataset.bsTriggerLabel}"` : '')}
                             data-bs-toggle="offcanvas" aria-controls="${submenu.id}">
                              ${anchor.innerHTML}
                              <i class="icon-chevron-right"></i>
                          </a>`
                ));
        }
    }

    return NavigationEvo;
});
